// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { parseStrandsChunk } from "./parsers/strands";
import { parseLanggraphChunk } from "./parsers/langgraph";
import { parseClaudeAgentSdkChunk } from "./parsers/claude-agent-sdk";
import { parseAguiChunk } from "./parsers/agui";
import { readSSEStream } from "./utils/sse";
/** Resolve parser from pattern prefix. Defaults to strands parser. */
function getParser(pattern) {
    if (pattern.startsWith("agui-"))
        return parseAguiChunk;
    if (pattern.startsWith("langgraph-"))
        return parseLanggraphChunk;
    if (pattern.startsWith("claude-"))
        return parseClaudeAgentSdkChunk;
    if (pattern.startsWith("strands-"))
        return parseStrandsChunk;
    return parseStrandsChunk;
}
/**
 * Hermes (React Native) has no crypto.randomUUID — guard with a
 * Math.random v4 fallback. These ids are trace/session identifiers, not
 * security material (found on-device 2026-08-24: Amy crashed on send).
 */
function safeUUID() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
}
/**
 * ADR-079: the id of one Amy turn, minted by the client BEFORE the send so
 * the turn can be looked up even if the stream dies before its first byte.
 */
export function generateTurnId() {
    return safeUUID();
}
export class AgentCoreClient {
    runtimeArn;
    region;
    pattern;
    parser;
    fetchImpl;
    constructor(config) {
        this.runtimeArn = config.runtimeArn;
        this.region = config.region ?? "us-east-1";
        this.pattern = config.pattern;
        this.parser = getParser(config.pattern);
        // React Native's global fetch cannot stream response bodies; the mobile
        // app injects expo/fetch here. Bind to preserve platform internals.
        this.fetchImpl = config.fetchImpl ?? ((...args) => fetch(...args));
    }
    generateSessionId() {
        return safeUUID();
    }
    async invoke(query, sessionId, accessToken, onEvent, options) {
        if (!accessToken)
            throw new Error("No valid access token found.");
        if (!this.runtimeArn)
            throw new Error("Agent Runtime ARN not configured.");
        const endpoint = `https://bedrock-agentcore.${this.region}.amazonaws.com`;
        const escapedArn = encodeURIComponent(this.runtimeArn);
        const url = `${endpoint}/runtimes/${escapedArn}/invocations?qualifier=DEFAULT`;
        const traceId = `1-${Math.floor(Date.now() / 1000).toString(16)}-${safeUUID()}`;
        // Build payload based on pattern — AG-UI protocol expects a different format
        const body = this.pattern.startsWith("agui-")
            ? {
                threadId: sessionId,
                runId: safeUUID(),
                messages: [{ id: safeUUID(), role: "user", content: query }],
                state: {},
                tools: [],
                context: [],
                forwardedProps: {},
            }
            : {
                prompt: query,
                runtimeSessionId: sessionId,
                // F5: optional image attachments ([{format, base64}]). Omitted
                // entirely when absent so the no-image wire shape is unchanged.
                ...(options?.images && options.images.length > 0 ? { images: options.images } : {}),
                ...(options?.documents && options.documents.length > 0
                    ? { documents: options.documents }
                    : {}),
                // #100: stable conversation id for history; runtime session id
                // above keeps pinning the container. Omitted → legacy behavior.
                ...(options?.conversationId ? { conversationId: options.conversationId } : {}),
                ...(options?.sessionRebound ? { sessionRebound: true } : {}),
                ...(options?.client ? { client: options.client } : {}),
                ...(options?.conversationSource ? { conversationSource: options.conversationSource } : {}),
                ...(options?.clientBoundVersion ? { clientBoundVersion: options.clientBoundVersion } : {}),
                ...(options?.locale ? { locale: options.locale } : {}),
                // Acceptance 4: which client BUILD sent this turn (AMY_TURN logs it).
                ...(options?.clientBuild ? { clientBuild: options.clientBuild } : {}),
                // ADR-079: the client-minted turn id (omitted → the runtime mints one).
                ...(options?.turnId ? { turnId: options.turnId } : {}),
            };
        // User identity is extracted server-side from the validated JWT token
        // (Authorization header), not sent in the payload body. This prevents
        // impersonation via prompt injection.
        const response = await this.fetchImpl(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Amzn-Trace-Id": traceId,
                "Content-Type": "application/json",
                "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        await readSSEStream(response, this.parser, onEvent);
    }
}
